// Volts kandidater: reservebottens rangering (fallback.rank) filtreret og vægtet efter spillerens
// ønsker og Haikus plan, så Jev kun skal vælge mellem 4 til 8 lovlige, fornuftige handlinger.
// Bygger også den kompakte state (højst 8 KB), som Jev og Haiku ser. Ren TS uden DOM.

import { clockFull } from '../sim/cities';
import { greenPct } from '../sim/state';
import { TICK_HZ } from '../sim/types';
import { seasonOf } from '../sim/weather';
import { lineById, nodeById, REINFORCE_COST } from '../sim/grid';
import { roleBlocked } from '../sim/roles';
import type { Candidate, CardOption, GridNode, PlayerId, State } from '../sim/types';
import { greenEnough, isPlant } from './coach';
import { cityMoves, moveKey, truthConflict } from './truth';
import type { CityMove } from './truth';
import { rank, scoreCandidate, metrics } from './fallback';
import type { ScoredCandidate } from './fallback';
import { DEFAULT_POLICY, LIMITS, planUnder } from './protocol';
import type { Plan, Policy, Region, WireCandidate, WireCity, WireRisk, WireState, WireThreat } from './protocol';

export interface VoltCandidate {
  cand: Candidate;
  /** Reservebottens score efter vægtning med plan og politik. */
  score: number;
  /** Reservebottens rå score. */
  base: number;
  wire: WireCandidate;
  /** Kort dansk tekst til tankepanelet. */
  label: string;
  /** Node-id'er handlingen handler om (til markør, region og cursor-afstand). */
  nodes: string[];
  /** Krise: en by er mørk eller har brugt halvdelen af sit mørkeur, og trækket hjælper. */
  rescue?: boolean;
  /**
   * Trækket er netop det, spillet selv råder til: en ny by får sin første ledning, eller byens kort og
   * hint siger det (station, vej nr. 2). Med roller er det Planlæggerens job, så Volt spørger ikke først
   * (IGN-løkke 2 runde 2: "Volt venter 14-28 s, før han bygger til en ny by").
   */
  hinted?: boolean;
}

/**
 * Skal spilleren sige ja først? Et nyt anlæg eller en opgradering, der ikke redder en by lige nu, er
 * spillerens valg (IGN runde 1: "Volt spiller spillet for dig", 15 af 21 ledninger). Redninger,
 * reparationer, master og trusler klarer Volt selv; "Byg løs" lader ham også bygge.
 */
export function needsYes(c: VoltCandidate, policy: Policy): boolean {
  if (c.rescue || c.hinted || policy.avoid.includes('wait')) return false;
  return BUILD_KINDS.has(c.cand.kind) || UPGRADE_KINDS.has(c.cand.kind);
}

export interface CandidateOptions {
  policy?: Policy;
  plan?: Plan | null;
  /** Volts egne seneste valg (straffes, så han ikke gentager sig). */
  recent?: ReadonlySet<string>;
  /** Mål spilleren har gang i lige nu (fx en ledning i køen). */
  locked?: ReadonlySet<string>;
  /** Sand, når noderne (eller midten mellem dem) er tæt på spillerens cursor de sidste 2 s. */
  nearCursor?: (nodes: readonly string[]) => boolean;
  /** Højst så mange ud over `wait` (standard 7). */
  max?: number;
  /** Allerede rangeret (til tests og genbrug). */
  ranked?: ScoredCandidate[];
  /** Hvem Volt spiller som (standard p2). Med roller får han kun sin egen rolles træk. */
  player?: PlayerId;
}

/** Så meget løfter det et træk, at det er netop det, kortet og hintet siger for en by i nød. */
const TRUTH_BONUS = 60;

// ---------------------------------------------------------------------------
// Geografi

/** Midten af nettet (median af aktive noder), så "vest" og "øst" passer til den bane man spiller. */
export function mapCenter(state: State): { x: number; y: number } {
  const ns = state.nodes.filter((n) => n.active && n.kind !== 'foreign');
  if (ns.length === 0) return { x: 0, y: 0 };
  const med = (v: number[]) => {
    const s = [...v].sort((a, b) => a - b);
    return s[Math.floor(s.length / 2)] as number;
  };
  return { x: med(ns.map((n) => n.x)), y: med(ns.map((n) => n.y)) };
}

export function regionOf(center: { x: number; y: number }, n: { x: number; y: number }): Exclude<Region, 'all'> {
  const dx = n.x - center.x;
  const dy = n.y - center.y;
  if (Math.abs(dx) >= Math.abs(dy)) return dx < 0 ? 'west' : 'east';
  return dy < 0 ? 'north' : 'south';
}

/** Noderne en kandidat handler om. */
export function candidateNodes(state: State, c: Candidate): string[] {
  const t = c.target ?? '';
  switch (c.kind) {
    case 'build_line':
      return t.split('|').filter(Boolean);
    case 'upgrade_line':
    case 'bury_line':
    case 'reinforce_mast':
    case 'dispatch_crew':
    case 'demolish': {
      const l = lineById(state, t);
      return l ? [l.a, l.b] : [];
    }
    case 'shoo_threat':
    case 'answer_card': {
      const th = state.threats.find((x) => x.id === t);
      if (!th) return [];
      const l = lineById(state, th.targetLine);
      if (l) return [l.a, l.b];
      return th.nodes?.slice(0, 2) ?? [];
    }
    case 'upgrade_station':
    case 'build_battery':
    case 'build_flywheel':
    case 'power_shed':
      return t ? [t] : [];
    default:
      return [];
  }
}

/** Kandidatens punkt på kortet (km), eller null for handlinger uden sted. */
export function candidatePoint(state: State, c: Candidate): { x: number; y: number } | null {
  const ns = candidateNodes(state, c)
    .map((id) => nodeById(state, id))
    .filter((n): n is GridNode => !!n);
  if (ns.length === 0) return null;
  return { x: ns.reduce((s, n) => s + n.x, 0) / ns.length, y: ns.reduce((s, n) => s + n.y, 0) / ns.length };
}

/** Navn som skemaet godtager: bogstaver, tal, mellemrum og få tegn, højst 24. */
export function wireName(s: string): string {
  return s.replace(/[^\p{L}\p{N} .'-]/gu, '').slice(0, LIMITS.name);
}

// ---------------------------------------------------------------------------
// Tekst

const MEDIUM_DA: Record<string, string> = { air: 'luftledning', cable: 'kabel', sea: 'søkabel' };
const CARD_DA: Record<CardOption, string> = {
  cable: 'Kabelstykke',
  meeting: 'Borgermøde',
  build: 'Byg alligevel',
  smart: 'Smart opladning',
  ride: 'Hold den ud',
  plan: 'Planlæg køling',
  reserve: 'Reservér plads',
  wait: 'Vent og se',
};

export function labelOf(state: State, c: Candidate): string {
  const names = candidateNodes(state, c).map((id) => nodeById(state, id)?.name ?? id);
  const [a = '', b = ''] = names;
  switch (c.kind) {
    case 'build_line':
      return `Byg ${MEDIUM_DA[c.opt ?? 'air'] ?? 'ledning'} ${a} til ${b}`;
    case 'upgrade_line':
      return `Opgradér ${a} til ${b}`;
    case 'bury_line':
      return `Grav ${a} til ${b} ned`;
    case 'reinforce_mast':
      return c.target === '*' ? 'Forstærk alle udsatte master' : `Forstærk master ${a} til ${b}`;
    case 'dispatch_crew':
      return `Reparér ${a} til ${b}`;
    case 'shoo_threat': {
      const th = state.threats.find((x) => x.id === c.target);
      // To både på én gang skal kunne skelnes: sig, hvilken ledning de truer.
      const where = a && b ? ` ved ${a} til ${b}` : '';
      return `${th?.kind === 'anchor' ? 'Jag ankerbåden væk' : 'Jag gravkoen væk'}${where}`;
    }
    case 'upgrade_station':
      return `Større station i ${a}`;
    case 'build_battery':
      return `Batteri i ${a}`;
    case 'build_flywheel':
      return `Svinghjul i ${a}`;
    case 'hire_crew':
      return 'Hyr et hold mere';
    case 'power_fcr':
      return 'Brug frekvensreserven';
    case 'trim_hz':
      return 'Regulér frekvensen';
    case 'power_norway':
      return 'Ring til Norge';
    case 'power_shed':
      return `Aflast ${a} et øjeblik`;
    case 'pick_pack':
      return `Vælg gave ${Number(c.opt ?? 0) + 1}`;
    case 'pick_both':
      return `Tag begge gaver for ${c.cost} kr`;
    case 'answer_card':
      return CARD_DA[(c.opt ?? 'wait') as CardOption] ?? 'Svar på kortet';
    case 'call_year':
      return 'Start næste år';
    case 'demolish':
      return `Riv ${a} til ${b} ned`;
    default:
      return 'Vent og spar';
  }
}

// ---------------------------------------------------------------------------
// Vægtning

const STORM_KINDS = new Set<Candidate['kind']>(['reinforce_mast']);
const BUILD_KINDS = new Set<Candidate['kind']>(['build_line', 'build_battery', 'build_flywheel']);
const UPGRADE_KINDS = new Set<Candidate['kind']>(['upgrade_line', 'upgrade_station', 'bury_line']);
const SAFETY_KINDS = new Set<Candidate['kind']>(['reinforce_mast', 'dispatch_crew', 'shoo_threat', 'hire_crew']);

/** Må Volt overhovedet foreslå kandidaten under denne politik? */
export function allowed(c: Candidate, policy: Policy): boolean {
  if (c.kind === 'wait') return true;
  const av = new Set(policy.avoid);
  if (av.has('storm') && STORM_KINDS.has(c.kind)) return false;
  if (av.has('cards') && c.kind === 'answer_card') return false;
  if (av.has('build') && BUILD_KINDS.has(c.kind)) return false;
  if (av.has('upgrade') && UPGRADE_KINDS.has(c.kind)) return false;
  if (av.has('spend') && c.cost > 0) return false;
  return true;
}

interface Weights {
  focus_region: Region;
  save_for: Policy['save_for'];
  priority: Policy['priority'];
}

function bonus(state: State, s: ScoredCandidate, nodes: string[], center: { x: number; y: number }, w: Weights, strength: number): number {
  const c = s.cand;
  if (c.kind === 'wait') {
    // At spare er et træk i sig selv, når planen eller spilleren siger det.
    return ((w.save_for !== 'nothing' ? 8 : 0) + (w.priority === 'money' ? 20 : 0)) * strength;
  }
  let b = 0;
  if (w.focus_region !== 'all' && nodes.length > 0) {
    const inside = nodes.some((id) => {
      const n = nodeById(state, id);
      return n ? regionOf(center, n) === w.focus_region : false;
    });
    b += inside ? 30 : -25;
  }
  switch (w.priority) {
    case 'cities':
      b += 25 * s.prevent + 0.3 * s.income;
      break;
    case 'green':
      b += 25 * s.green;
      break;
    case 'safety':
      if (SAFETY_KINDS.has(c.kind)) b += 15;
      break;
    case 'money':
      b -= 0.5 * s.price;
      break;
    default:
      break;
  }
  // Det, man sparer op til, får ingen bonus: før gav det +8, så "spar" fik Volt til at bruge mere.
  if (w.save_for !== 'nothing' && s.price > 0 && s.prevent <= 0 && !savingFor(c.kind, w.save_for)) b -= 0.6 * s.price;
  return b * strength;
}

/** Er handlingen netop det, der spares op til? */
function savingFor(kind: Candidate['kind'], save: Policy['save_for']): boolean {
  if (save === 'storm') return SAFETY_KINDS.has(kind);
  if (save === 'new_city') return kind === 'build_line';
  if (save === 'upgrade') return UPGRADE_KINDS.has(kind);
  if (save === 'battery') return kind === 'build_battery';
  return false;
}

/**
 * Spilleren bad Volt spare: alt, der koster uden at redde en by nu, er ude, medmindre det er
 * netop det, der spares op til. Kun spillerens ønske er hårdt; Haikus plan vægter bare.
 */
export function spendsAgainst(s: ScoredCandidate, policy: Policy): boolean {
  if (policy.priority !== 'money' || s.cand.kind === 'wait' || s.price <= 0 || s.prevent > 0) return false;
  return !savingFor(s.cand.kind, policy.save_for);
}

/** Top `max` kandidater plus `wait`, vægtet efter politik (fuld styrke) og plan (halv styrke). */
export function buildCandidates(state: State, opts: CandidateOptions = {}): VoltCandidate[] {
  const policy = opts.policy ?? DEFAULT_POLICY;
  const max = opts.max ?? 7;
  const player = opts.player ?? 'p2';
  const ranked = opts.ranked ?? rank(state, player, opts.recent ?? new Set());
  const center = mapCenter(state);
  // Spilleren har sidste ord: planen vægter kun dér, hvor den ikke strider mod ønsket.
  const plan = planUnder(opts.plan ?? null, policy);
  const build = policy.avoid.includes('wait');
  const out: VoltCandidate[] = [];
  let wait: VoltCandidate | null = null;
  let green: boolean | null = null;
  const crisis = inCrisis(state);
  const helps = new Set<string>();
  // Én sandhed: byernes træk, som kortet og hintet viser dem. Dem i Volts rolle løftes; alt andet,
  // der bygger til en by i nød, er ude.
  const truth = new Map<string, CityMove>();
  for (const m of cityMoves(state)) {
    if (!m.ok || (m.owner !== null && m.owner !== player)) continue;
    const k = moveKey(m.move.kind, m.move.target);
    if (!truth.has(k)) truth.set(k, m);
  }
  const seen = new Set<string>();
  const pass = (c: Candidate, nodes: string[]): boolean => {
    if (c.kind === 'wait') return true;
    if (opts.locked && (opts.locked.has(c.target ?? '') || nodes.some((n) => opts.locked?.has(n)))) return false;
    if (nodes.length > 0 && opts.nearCursor?.(nodes)) return false;
    return true;
  };
  for (const s of ranked) {
    const c = s.cand;
    // Roller: Volt rører kun sin egen rolles værktøj, også i "Byg løs".
    if (c.kind !== 'wait' && roleBlocked(state, c.kind, player)) continue;
    // Kortene og "Start næste år" er spillerens: Volt giver sit bud på kort, men svarer aldrig selv,
    // og et nyt år for hele holdet starter kun, når spilleren trykker.
    // Frekvensnålen er en refleks i kontrolløkken (controller.chore), ikke et træk at tænke over.
    if (c.kind === 'answer_card' || c.kind === 'call_year' || c.kind === 'trim_hz' || !allowed(c, policy) || spendsAgainst(s, policy)) continue;
    if (c.kind === 'hire_crew' && !crewNeeded(state)) continue;
    const nodes = candidateNodes(state, c);
    if (!pass(c, nodes)) continue;
    const key = moveKey(c.kind, c.target);
    const tm = truth.get(key);
    // Et andet træk til en by i nød, end kortet siger, er en modsigelse: ude. Det gælder også en anden
    // bys træk, der rører en nabo i nød med sit eget træk.
    if (c.kind !== 'wait' && truthConflict(state, c, nodes)) continue;
    if (tm) {
      seen.add(key);
      const vc = truthCand(state, c, s.score, nodes, tm, policy);
      helps.add(c.id);
      out.push(vc);
      continue;
    }
    // IGN runde 2: "Tag vest" er et ønske fra spilleren, ikke en vægt. Uden for området handler Volt
    // kun for at redde en by.
    if (c.kind !== 'wait' && policy.focus_region !== 'all' && nodes.length > 0 && s.prevent <= 0) {
      const inside = nodes.some((id) => {
        const n = nodeById(state, id);
        return n ? regionOf(center, n) === policy.focus_region : false;
      });
      if (!inside) continue;
    }
    // Kul er sidste udvej: et værk er kun på listen, når det redder en by, og vind og sol ikke kan
    // (IGN runde 2: "Volt foreslog kul igen").
    if (c.kind === 'build_line' && nodes.some((id) => isPlantId(state, id))) {
      green ??= greenEnough(state);
      if (s.prevent <= 0 || green) continue;
    }
    // "Byg løs" holder en lille reserve til stormen, medmindre trækket redder en by.
    if (build && c.kind !== 'wait' && !SAFETY_KINDS.has(c.kind) && s.price > 0 && s.prevent <= 0 && state.budget - s.price < BUILD_RESERVE) continue;
    let score = s.score + bonus(state, s, nodes, center, policy, 1);
    if (plan) score += bonus(state, s, nodes, center, plan, 0.5);
    // "Byg løs": at stå stille er det sidste, Volt gør, og en by uden ledning kommer først
    // (IGN runde 2: "Byg løs" lod Herning stå uden strøm).
    if (c.kind === 'wait' && build) score -= 15;
    // IGN runde 4: Volt lod Struer og Herning stå mørke, mens planen sagde "Herning mangler strøm".
    // En by uden ledning er Volts første job i alle tilstande; i "Byg løs" endnu mere.
    const lonely = c.kind === 'build_line' && nodes.some((id) => isLonelyCity(state, id));
    // Rigtige byer før datacentre: et datacenter er en kunde, en by er et løfte (runde 2: "Volt
    // prioriterer datacentre over byer").
    const town = lonely && nodes.some((id) => isLonelyCity(state, id) && nodeById(state, id)?.kind === 'city');
    if (lonely) score += town ? (build ? 45 : 30) : 8;
    // "Byg løs" uden spaghetti: en ledning mellem to noder, der allerede har strøm og ingen nød, er sidst.
    if (build && c.kind === 'build_line' && !lonely && s.prevent <= 0 && nodes.every((id) => state.lines.some((l) => l.a === id || l.b === id))) score -= 12;
    // En by i nød går forud for alt andet (IGN runde 1: "Vent og spar 72 %", mens Thyborøn gik i sort).
    if (s.prevent > 0 && c.kind !== 'wait') score += 20;
    const vc = toVolt(state, c, score, s.score, nodes);
    // Kun med roller: så er byggeriet Volts eget job. Uden roller er han hjælperen, der spørger først.
    if (town && state.roles) vc.hinted = true;
    if (s.prevent > 0) helps.add(c.id);
    if (s.prevent > 0 && crisis) vc.rescue = true;
    if (c.kind === 'wait') wait = vc;
    else out.push(vc);
  }
  // Byernes træk, som rangeringen ikke havde med (fx en opgradering, reservebotten ikke regnede på).
  for (const [key, m] of truth) {
    if (seen.has(key)) continue;
    const c: Candidate = { id: `${m.move.kind}:${m.move.target ?? ''}:${m.move.opt ?? ''}`, kind: m.move.kind, cost: m.cost };
    if (m.move.target !== undefined) c.target = m.move.target;
    if (m.move.opt !== undefined) c.opt = m.move.opt;
    if (!allowed(c, policy)) continue;
    const nodes = candidateNodes(state, c);
    if (!pass(c, nodes) || truthConflict(state, c, nodes)) continue;
    helps.add(c.id);
    out.push(truthCand(state, c, scoreCandidate(state, metrics(state), c, opts.recent ?? new Set()).score, nodes, m, policy));
  }
  out.sort((a, b) => b.score - a.score || (a.cand.id < b.cand.id ? -1 : 1));
  const top = out.slice(0, max);
  // Kan et træk redde en by eller tænde en ny, er "vent" det sidste, Volt gør.
  if (wait && top.some((c) => helps.has(c.cand.id) || (c.cand.kind === 'build_line' && candidateNodes(state, c.cand).some((id) => isLonelyCity(state, id))))) {
    wait.score -= 40;
    wait.wire.score = Math.round(wait.score);
  }
  // Bygger Volt løs og har mindst to fornuftige træk, er "vent" slet ikke på listen.
  if (build && top.filter((c) => c.score > 0).length >= 2) return top;
  top.push(wait ?? toVolt(state, { id: 'wait::', kind: 'wait', cost: 0 }, 0, 0, []));
  return top;
}

/** Byens træk som kandidat: øverst, og en redning, når byens ur løber. */
function truthCand(state: State, c: Candidate, base: number, nodes: string[], m: CityMove, policy: Policy): VoltCandidate {
  const vc = toVolt(state, c, Math.max(base, 0) + TRUTH_BONUS + bonus(state, { cand: c, score: base, prevent: 1, green: 0, income: 0, price: c.cost }, nodes, mapCenter(state), policy, 1), base, nodes);
  const city = m.city.city;
  if (city && (city.out || city.clock > 0)) vc.rescue = true;
  if (state.roles) vc.hinted = true;
  return vc;
}

/** Så meget lader "Byg løs" stå i kassen: to forstærkede master. */
const BUILD_RESERVE = 2 * REINFORCE_COST;

/** En by på kortet, der endnu ikke har nogen ledning. */
function isLonelyCity(state: State, id: string): boolean {
  const n = nodeById(state, id);
  return !!n?.city && n.active && !n.phantom && !state.lines.some((l) => l.a === id || l.b === id);
}

/** Er en by mørk, eller har den brugt halvdelen af sit mørkeur? Så handler Volt uden at spørge. */
export function inCrisis(state: State): boolean {
  const half = clockFull(state) / 2;
  return state.nodes.some((n) => n.active && !n.phantom && !!n.city && (n.city.out || n.city.clock >= half));
}

function isPlantId(state: State, id: string): boolean {
  const n = nodeById(state, id);
  return !!n && isPlant(n);
}

/** Et hold mere giver kun mening, når der er noget at reparere, eller nettet er vokset lidt. */
export function crewNeeded(state: State): boolean {
  const broken = state.lines.filter((l) => l.status === 'broken' || l.halfDown).length;
  const idle = state.crews.filter((c) => c.kind === 'van' && c.task === 'idle').length;
  if (broken > idle) return true;
  return state.lines.filter((l) => l.owner !== 'level').length >= 4;
}

function toVolt(state: State, c: Candidate, score: number, base: number, nodes: string[]): VoltCandidate {
  const wire: WireCandidate = { id: c.id, kind: c.kind, cost: Math.round(c.cost), score: Math.round(score) };
  if (c.target !== undefined) wire.target = c.target;
  if (c.opt !== undefined) wire.opt = c.opt;
  const names = nodes
    .map((id) => nodeById(state, id)?.name)
    .filter((n): n is string => !!n)
    .map(wireName)
    .slice(0, 2);
  if (names.length > 0) wire.names = names;
  return { cand: c, score, base, wire, label: labelOf(state, c), nodes };
}

/** Kortsvar til et hændelseskort, rangeret af reservebotten. */
export function cardCandidates(state: State, threatId: string): VoltCandidate[] {
  const t = state.threats.find((x) => x.id === threatId);
  if (!t?.card || t.card.answer) return [];
  const base = metrics(state);
  const out: VoltCandidate[] = [];
  for (const o of t.card.options) {
    const c: Candidate = { id: `answer_card:${threatId}:${o}`, kind: 'answer_card', target: threatId, opt: o, cost: 0 };
    const s = scoreCandidate(state, base, c);
    out.push(toVolt(state, c, s.score, s.score, candidateNodes(state, c)));
  }
  return out.sort((a, b) => b.score - a.score);
}

// ---------------------------------------------------------------------------
// Kompakt state til Jev og Haiku

export interface WireStateOptions {
  plan?: Plan | null;
  policy?: Policy;
  recent?: string[];
  /** Volts plads: med roller bliver den til Volts rolle i STATE (p1 planlægger, p2 driftsvagt). */
  player?: PlayerId;
}

function clamp(v: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, Math.round(Number.isFinite(v) ? v : 0)));
}

export function buildWireState(state: State, opts: WireStateOptions = {}): WireState {
  const center = mapCenter(state);
  const full = Math.max(1, clockFull(state));
  const cities: WireCity[] = state.nodes
    .filter((n) => n.active && !n.phantom && n.city)
    .map((n) => {
      const c = n.city!;
      return {
        id: n.id.slice(0, LIMITS.name),
        name: wireName(n.name),
        dem: clamp(c.demandMw, 0, 100_000),
        srv: clamp(c.servedMw, 0, 100_000),
        clock: clamp((c.clock / full) * 100, 0, 100),
        shield: clamp(n.shield, 0, 2),
        region: regionOf(center, n),
      };
    })
    .sort((a, b) => b.clock - a.clock || b.dem - b.srv - (a.dem - a.srv))
    .slice(0, LIMITS.cities);

  const threatsLive = state.threats.filter((t) => t.phase !== 'done');
  const risks: WireRisk[] = [];
  const name = (id: string) => wireName(nodeById(state, id)?.name ?? id);
  for (const l of state.lines) {
    let why: WireRisk['why'] | null = null;
    const hit = threatsLive.find((t) => t.atRisk.includes(l.id) || t.targetLine === l.id);
    if (l.status === 'broken' || l.halfDown) why = 'broken';
    else if (hit && (hit.kind === 'storm' || hit.kind === 'digger' || hit.kind === 'anchor')) why = hit.kind;
    else if (l.loadPm >= 850) why = 'overload';
    if (why) risks.push({ line: l.id.slice(0, LIMITS.name), a: name(l.a), b: name(l.b), load: clamp(l.loadPm, 0, 1000), why });
  }
  const order: Record<WireRisk['why'], number> = { broken: 0, storm: 1, digger: 2, anchor: 3, overload: 4, single: 5 };
  risks.sort((a, b) => order[a.why] - order[b.why] || b.load - a.load);

  const threats: WireThreat[] = threatsLive.slice(0, LIMITS.threats).map((t) => ({
    id: t.id.slice(0, LIMITS.name),
    kind: t.kind,
    in: clamp((t.hitAt - state.clockTick) / TICK_HZ, -600, 600),
    lines: (t.atRisk.length > 0 ? t.atRisk : t.targetLine ? [t.targetLine] : []).slice(0, 6).map((x) => x.slice(0, LIMITS.name)),
  }));

  const s: WireState = {
    level: state.levelId.slice(0, 40),
    year: clamp(state.year, 0, 20),
    season: seasonOf(state.clockTick),
    budget: clamp(state.budget, -100_000, 1_000_000),
    trust: clamp(state.trust, 0, 100),
    green: clamp(greenPct(state), 0, 100),
    coop: state.coop,
    cities,
    risks: risks.slice(0, LIMITS.risks),
    threats,
  };
  if (opts.plan) s.plan = { ...opts.plan, note: opts.plan.note.slice(0, LIMITS.note) };
  if (opts.policy) s.policy = opts.policy;
  if (opts.recent && opts.recent.length > 0) s.recent = opts.recent.slice(-LIMITS.recent);
  if (state.roles && opts.player) s.role = opts.player === 'p1' ? 'planner' : 'drift';
  // Sikring: hold dig under 8 KB, også på en kæmpe bane.
  while (bytes(s) > LIMITS.stateBytes - 256 && s.cities.length > 4) s.cities = s.cities.slice(0, s.cities.length - 4);
  return s;
}

export function bytes(v: unknown): number {
  return new TextEncoder().encode(JSON.stringify(v)).length;
}
